import { useCallback, useEffect, useRef, useState } from "react";

/** Producer input gap after which the control page stops talking to the API. */
export const CONTROL_IDLE_MS = 30 * 60 * 1000;

const INPUT_EVENTS = ["pointerdown", "keydown", "wheel", "touchstart"] as const;

/**
 * Control-page presence: `paused` is true while the tab is hidden (polling
 * resumes the moment it is visible again) or after `idleMs` without producer
 * input (stays paused until `reconnect()`; the page shows a Reconnect button).
 */
export function useControlActivity(idleMs = CONTROL_IDLE_MS) {
  const [hidden, setHidden] = useState(
    typeof document !== "undefined" && document.hidden,
  );
  const [idle, setIdle] = useState(false);
  const idleRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const arm = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      idleRef.current = true;
      setIdle(true);
    }, idleMs);
  }, [idleMs]);

  useEffect(() => {
    arm();
    const onInput = () => {
      // Once idle, only the Reconnect button brings the page back.
      if (!idleRef.current) arm();
    };
    const onVisibility = () => setHidden(document.hidden);
    INPUT_EVENTS.forEach((e) =>
      document.addEventListener(e, onInput, { passive: true }),
    );
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      INPUT_EVENTS.forEach((e) => document.removeEventListener(e, onInput));
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [arm]);

  const reconnect = useCallback(() => {
    idleRef.current = false;
    setIdle(false);
    arm();
  }, [arm]);

  return { hidden, idle, paused: hidden || idle, reconnect };
}
